import {track} from '@getmoss/moss-analytics'
import type {LinkField, TableField} from '@prismicio/client'
import * as prismic from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicRichText, PrismicTable} from '@prismicio/react'
import cn from 'classnames'
import {AnimatePresence, motion} from 'framer-motion'
import type {
  BlogPostSliceDefaultPrimaryBannerItem,
  BlogPostSliceDefaultPrimaryFaqSectionItem,
  BlogPostSliceDefaultPrimaryListSectionItem,
  BlogPostSliceDefaultPrimaryTableItem,
  BlogPostSliceDefaultPrimaryTakeawaysSectionItem,
  ButtonDocument,
} from 'prismicio-types'
import type {ReactNode} from 'react'
import {forwardRef, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import type {PrismicNode} from 'slices/AnimatedTextBlock/components/types'
import {ButtonV2} from 'src/common/ButtonV2'
import {getButtonsData, useTypewriterSegments} from 'src/common/helpers'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {Icon} from 'src/common/Icon'
import {DOWNLOAD_SKILL_ANCHOR} from '../constants'
import {DownloadRepositorySection} from './DownloadRepositorySection'
import {TabSwitcher} from './TabSwitcher'
import {useScrollRevealScale} from './useScrollRevealScale'

export type QuoteItem = {
  rte_label_content: prismic.KeyTextField
  quote: prismic.KeyTextField
  author_name: prismic.KeyTextField
  author_role: prismic.KeyTextField
  author_image?: prismic.ImageField<never>
  exclude_quotation_mark?: prismic.BooleanField
}

type PostSections = {
  takeaways: BlogPostSliceDefaultPrimaryTakeawaysSectionItem[]
  list: BlogPostSliceDefaultPrimaryListSectionItem[]
  banner: BlogPostSliceDefaultPrimaryBannerItem[]
  faq: BlogPostSliceDefaultPrimaryFaqSectionItem[]
  table: BlogPostSliceDefaultPrimaryTableItem[]
  skillDownloadLink?: LinkField
  chatgptPromptDownloadLink?: LinkField
  githubRepositoryLink?: LinkField
  quotes?: QuoteItem[]
}

export type ParagraphNode = PrismicNode & {text: string}

export const ParagraphComponent = ({
  children,
  node,
  sections,
  buttonsData,
}: {
  children: ReactNode
  node: ParagraphNode
  sections: PostSections
  buttonsData?: ButtonDocument[]
}) => {
  const sectionLabel = node?.spans?.[0]?.data?.label
  const textContent = typeof children === 'string' ? children : node.text || ''

  if (sectionLabel) {
    switch (sectionLabel) {
      case 'list': {
        const matchingList = sections.list.find((item) => item.list_id === textContent)
        return matchingList ? <ListSection data={matchingList} /> : null
      }

      case 'banner': {
        const matchingBanner = sections.banner.find((item) => item.banner_id === textContent)
        return matchingBanner ? <BannerSection data={matchingBanner} buttonsData={buttonsData} /> : null
      }

      case 'table': {
        const matchingTable = sections.table.find((item) => item.table_id === textContent)
        return matchingTable ? <TableSection field={matchingTable.table_content} /> : null
      }

      case 'takeaways':
        return <TakeawaysSection data={sections.takeaways[0]} />
      case 'faq':
        return <FaqSection data={sections.faq} />

      case 'download_skill':
        if (!isFilled.link(sections.skillDownloadLink) || !isFilled.link(sections.chatgptPromptDownloadLink)) {
          return null
        }

        return (
          <DownloadSkillSection
            skillDownloadLink={sections.skillDownloadLink}
            chatgptPromptDownloadLink={sections.chatgptPromptDownloadLink}
          />
        )

      case 'download_repository':
        if (!isFilled.link(sections.githubRepositoryLink)) {
          return null
        }

        return <DownloadRepositorySection githubRepositoryLink={sections.githubRepositoryLink} />

      case 'quote': {
        const matchingQuote = sections.quotes?.find((item) => item.rte_label_content === textContent)
        return matchingQuote ? <QuoteSection data={matchingQuote} /> : null
      }

      default:
        return <p className="text-tg-body-l text-moss-grey-550">{children}</p>
    }
  } else {
    return <p className="text-tg-body-l text-moss-grey-550">{children}</p>
  }
}

const TakeawaysSection = ({data}: {data: BlogPostSliceDefaultPrimaryTakeawaysSectionItem}) => (
  <div className="flex flex-col gap-4 pt-4 lg:pt-8">
    {isFilled.keyText(data.title) && <h3 className="text-24 font-medium leading-10">{data.title}</h3>}
    <div className="flex w-full flex-col gap-6 rounded-lg border border-[#57A6FF] bg-[#F0F7FF] p-6">
      <PrismicRichText
        components={{
          list: ({children}) => <ol className="flex flex-col gap-6 [counter-reset:takeaway-counter]">{children}</ol>,
          listItem: ({children}) => (
            <li className="flex items-start gap-6 [counter-increment:takeaway-counter] before:flex before:size-5 before:shrink-0 before:flex-col before:items-start before:justify-center before:text-[10px] before:font-semibold before:uppercase before:leading-normal before:tracking-[0.6px] before:text-[#288CFC] before:content-[counter(takeaway-counter,decimal-leading-zero)]">
              <div className="flex flex-1 flex-col gap-1 text-[15px] font-normal leading-[20px] text-moss-black/[60%]">
                {children}
              </div>
            </li>
          ),
          strong: ({children}) => (
            <strong className="w-full text-[15px] font-medium leading-[20px] text-moss-black">{children}</strong>
          ),
        }}
        field={data.list}
      />
    </div>
  </div>
)

const ListSection = ({data}: {data: BlogPostSliceDefaultPrimaryListSectionItem}) => (
  <div className="flex flex-col gap-4 py-4 lg:py-8">
    {isFilled.keyText(data.title) && <h3 className="text-24 font-medium leading-10">{data.title}</h3>}
    <div className="flex w-full flex-col gap-6 rounded-lg border border-[rgba(19,18,18,0.1)] bg-[#F7F7F7] p-6">
      <PrismicRichText
        components={{
          list: ({children}) => <ul className="flex flex-col gap-6">{children}</ul>,
          listItem: ({children}) => (
            <li className="flex items-start gap-6 before:text-[14px] before:font-medium before:leading-[20px] before:tracking-[0.28px] before:text-[#A8A8A8] before:content-['✓']">
              <div className="flex flex-1 flex-col gap-1 text-[15px] font-normal leading-[20px] text-moss-black/[60%]">
                {children}
              </div>
            </li>
          ),
          strong: ({children}) => (
            <strong className="w-full text-[15px] font-medium leading-[20px] text-[#131212]">{children}</strong>
          ),
        }}
        field={data.list}
      />
    </div>
  </div>
)

const BannerSection = ({
  data,
  buttonsData,
}: {
  data: BlogPostSliceDefaultPrimaryBannerItem
  buttonsData?: ButtonDocument[]
}) => {
  const {primaryButtonData} = getButtonsData({buttonsData: buttonsData, primaryButton: data.button})

  return (
    <div className="flex flex-col gap-4 pt-4 lg:pt-8">
      <div className="flex w-full items-center gap-6 overflow-hidden rounded-lg bg-[#002414]">
        <div className="flex min-h-[200px] flex-1 flex-col justify-between gap-6 p-6">
          <div className="flex flex-col gap-4">
            {isFilled.richText(data.tag) && (
              <PrismicRichText
                components={{
                  paragraph: ({children}) => (
                    <p className="text-[10px] font-semibold uppercase leading-normal tracking-[0.6px] text-moss-white">
                      {children}
                    </p>
                  ),
                  strong: ({children}) => <span className="text-green-neon">{children}</span>,
                }}
                field={data.tag}
              />
            )}
            {isFilled.richText(data.headline) && (
              <PrismicRichText
                components={{
                  heading4: ({children}) => (
                    <h4 className="text-[24px] font-medium leading-[30px] text-moss-white">{children}</h4>
                  ),
                }}
                field={data.headline}
              />
            )}
          </div>
          {primaryButtonData && (
            <ButtonV2
              trackingData={{
                mktgId: 'blog-post-banner-section-button',
                dataType: 'blog-post-banner-section-button',
                location: 'blog-post-banner-section',
                pageName: 'blog-post',
              }}
              newTrackingProps={{
                interaction: {
                  element: `banner-section-button`,
                  elementText: primaryButtonData?.label || 'unknown',
                  location: `blog-post-banner-section`,
                },
                pageType: 'blog-post',
              }}
              variant="primary"
              className="text-button-s !h-[38px] w-fit"
            >
              {primaryButtonData?.label}
            </ButtonV2>
          )}
        </div>
        {isFilled.image(data.image) && (
          <div className="relative h-[200px] w-[296px] shrink-0 overflow-hidden max-md:hidden">
            <img src={data.image.url} alt={data.image.alt || ''} className="size-full object-cover object-center" />
          </div>
        )}
      </div>
    </div>
  )
}

const FaqSection = ({data}: {data: BlogPostSliceDefaultPrimaryFaqSectionItem[]}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <div className="flex flex-col gap-4 pb-12 pt-8">
      <h3 className="text-[24px] font-medium leading-[40px] text-[#131212]">FAQs</h3>
      <div className="flex flex-col gap-4">
        {data.map((item, index) => (
          <div key={index} className="flex flex-col border-b border-[#E3E2E2]">
            <button onClick={() => toggleAccordion(index)} className="flex w-full items-start gap-6 pb-4 text-left">
              <div className="flex flex-1 flex-col gap-1">
                <PrismicRichText
                  components={{
                    heading6: ({children}) => (
                      <h4 className="text-[18px] font-medium leading-[24px] tracking-[0.18px] text-[#131212]">
                        {children}
                      </h4>
                    ),
                    paragraph: ({children}) => (
                      <p
                        className={cn(
                          'text-[15px] font-normal leading-[20px] text-[#5B5858] transition-all duration-700 ease-in-out',
                          {
                            'max-h-0 overflow-hidden': openIndex !== index,
                            'max-h-[200px]': openIndex === index,
                          },
                        )}
                      >
                        {children}
                      </p>
                    ),
                  }}
                  field={item.question_and_answer}
                />
              </div>
              <div className="flex size-8 shrink-0 items-center justify-center rounded border border-[rgba(19,18,18,0.1)] bg-moss-white lg:hover:hover:bg-moss-grey-100">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  className={`transition-transform ${openIndex === index ? 'rotate-90' : 'rotate-[270deg]'}`}
                >
                  <path
                    d="M5.25 3.5L8.75 7L5.25 10.5"
                    stroke="#131212"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

const QuoteSection = ({data}: {data: QuoteItem}) => (
  <div className="my-4 flex flex-col gap-6 rounded-lg bg-moss-grey-100 p-5 md:py-6 md:pl-8 md:pr-12">
    <div className="flex flex-col items-start gap-2 md:flex-row md:gap-6">
      {!data.exclude_quotation_mark && (
        <div
          aria-hidden="true"
          className="relative flex size-8 min-h-8 min-w-8 items-center justify-center rounded-[4px] bg-[#B9FF3E]"
        >
          <Icon name="quotation-mark" className="size-5 min-w-5 text-[#1E1D1D]" />
          <div className="absolute inset-0 rounded-[4px] border border-[rgba(0,0,0,.04)]" />
        </div>
      )}
      <p className="md:text-h4 text-[20px] font-medium leading-7 text-moss-black">{data.quote}</p>
    </div>
    <div className={`flex items-center gap-3 ${data.exclude_quotation_mark ? '' : 'md:pl-11'}`}>
      {isFilled.image(data.author_image) && (
        <img
          src={data.author_image.url}
          alt={data.author_image.alt || data.author_name || ''}
          className="size-10 rounded-full object-cover"
        />
      )}
      <div className="flex flex-col self-end">
        {isFilled.keyText(data.author_role) && (
          <span className="text-13 font-medium leading-normal tracking-[.13px] text-moss-grey-300">
            {data.author_role}
          </span>
        )}
        {isFilled.keyText(data.author_name) && (
          <span className="text-13 font-medium leading-normal tracking-[.13px] text-moss-black">
            {data.author_name}
          </span>
        )}
      </div>
    </div>
  </div>
)

const InlineCode = forwardRef<HTMLElement, {children: ReactNode}>(({children}, ref) => (
  <code ref={ref} className="inline rounded bg-[#ffffff1f] px-1 py-0.5 text-[10px] text-moss-white">
    {children}
  </code>
))

const TypewriterCode = ({text}: {text: string}) => {
  const [isInView, setIsInView] = useState(false)
  const ref = useRef<HTMLElement>(null)

  // Start typing only when the element enters the viewport
  useEffect(() => {
    const el = ref.current

    if (!el) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)

          observer.disconnect()
        }
      },
      {threshold: 0.5},
    )

    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  const segments = isInView ? [{text, isBold: false}] : []
  const {renderTypewriterText} = useTypewriterSegments(segments, 'normal')

  return (
    <InlineCode ref={ref}>
      {renderTypewriterText()}
      <span className="ml-0.5 inline-block h-[0.8em] w-[1.5px] animate-cursor-blink bg-moss-white align-[-1px]" />
    </InlineCode>
  )
}

// Keeps each DownloadSkillSection instance's motion.div layoutId unique so framer-motion
// doesn't treat multiple sections on the same page as the same shared element
let downloadSkillInstanceCounter = 0

export const DownloadSkillSection = ({
  skillDownloadLink,
  chatgptPromptDownloadLink,
}: {
  skillDownloadLink: LinkField
  chatgptPromptDownloadLink: LinkField
}) => {
  const [activeTab, setActiveTab] = useState<'claude' | 'chatgpt'>('claude')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const domainCountry = useGetDomainCountry()
  const instanceIdRef = useRef<number>()
  const {ref: chromeRef, scale: scrollScale} = useScrollRevealScale()

  if (instanceIdRef.current === undefined) {
    instanceIdRef.current = ++downloadSkillInstanceCounter
  }

  const windowLayoutId = `download-skill-window-${instanceIdRef.current}`

  // Mount on client side so the fullscreen portal target is available
  useEffect(() => {
    setMounted(true)
  }, [])

  // Disable scrolling while fullscreen. Locking both html and body (rather than body alone)
  // covers browsers where the scrolling container is the root element. Avoid touching
  // `position`/`top` here — that shifts the body's layout origin and breaks the shared
  // layoutId transition framer-motion uses to animate between the compact and fullscreen views.
  useEffect(() => {
    if (!isFullscreen) {
      return undefined
    }

    const originalBodyOverflow = document.body.style.overflow
    const originalHtmlOverflow = document.documentElement.style.overflow

    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = originalBodyOverflow
      document.documentElement.style.overflow = originalHtmlOverflow
    }
  }, [isFullscreen])

  // Exit fullscreen on Escape key
  useEffect(() => {
    if (!isFullscreen) {
      return undefined
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFullscreen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)

    return () => document.removeEventListener('keydown', handleEscape)
  }, [isFullscreen])

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev)
  }

  const switchTab = (tab: 'claude' | 'chatgpt') => {
    setActiveTab(tab)

    track({
      event: 'interaction',
      interaction: {
        type: 'click',
        element: 'download-skill-tab',
        elementText: tab,
        location: 'blog-post-download-skill-section',
      },
      page: {
        domainCountry,
        type: 'blog-post',
      },
    })
  }

  const skillUrl = isFilled.link(skillDownloadLink) ? prismic.asLink(skillDownloadLink) : null
  const chatgptPromptUrl = isFilled.link(chatgptPromptDownloadLink) ? prismic.asLink(chatgptPromptDownloadLink) : null

  const triggerDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url)

      if (!response.ok) {
        window.open(url, '_blank', 'noopener,noreferrer')
        return
      }

      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')

      a.href = blobUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  const getFilename = (link: LinkField): string => ('name' in link && typeof link.name === 'string' ? link.name : '')

  const skillName = getFilename(skillDownloadLink)
  const skillNameWithoutZip = getFilename(skillDownloadLink).replace(/\.zip$/i, '')
  const chatgptPromptName = getFilename(chatgptPromptDownloadLink)

  const handleDownload = async () => {
    if (skillUrl) {
      await triggerDownload(skillUrl, skillName)
    }

    if (activeTab === 'chatgpt') {
      if (chatgptPromptUrl) {
        await triggerDownload(chatgptPromptUrl, chatgptPromptName)
      }
    }
  }

  const steps =
    activeTab === 'claude'
      ? [
          <span key="claude-step1">
            Download the skill <InlineCode>.zip</InlineCode>
          </span>,
          <span key="claude-step2">
            Upload it in{' '}
            <a
              href="https://claude.ai/new#settings/customize-skills"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-neon underline"
            >
              claude.ai
            </a>{' '}
            as a skill
          </span>,
          <span key="claude-step3">
            Call it in chat as <TypewriterCode text={`/${skillNameWithoutZip}`} />
          </span>,
        ]
      : [
          <span key="chatgpt-step1">
            Download the skill <InlineCode>.zip</InlineCode> and{' '}
            <InlineCode>{getFilename(chatgptPromptDownloadLink)}</InlineCode>
          </span>,
          <span key="chatgpt-step2">
            Paste the prompt into{' '}
            <a
              href="https://chatgpt.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-neon underline"
            >
              chatgpt.com
            </a>
          </span>,
          <span key="chatgpt-step3">
            Attach your transactions and <TypewriterCode text={skillName} />
          </span>,
        ]

  const demoVideos = {
    claude: {
      src: 'https://mosswebsite.cdn.prismic.io/mosswebsite/4IKcFx3QQOFNvtVF_claude_skill_upload.mp4',
      label: 'claude_skill_upload.mp4',
    },
    chatgpt: {
      src: 'https://mosswebsite.cdn.prismic.io/mosswebsite/O8H5EIbMd87ETlzw_chatgpt_skill_upload.mp4',
      label: 'chatgpt_skill_upload.mp4',
    },
  }

  const tabSwitcher = (
    <TabSwitcher
      className="sm:w-[220px]"
      activeValue={activeTab}
      onChange={switchTab}
      options={[
        {value: 'claude', label: 'Claude', icon: 'anthropic-logo', iconClassName: 'size-3.5'},
        {value: 'chatgpt', label: 'ChatGPT', icon: 'chatgpt-logo', iconClassName: 'size-3.5'},
      ]}
    />
  )

  const downloadButton = (
    <ButtonV2
      onClick={handleDownload}
      variant="primary"
      className="text-button-xs !h-8 self-start"
      trackingData={{
        mktgId: 'blog-post-download-skill-button',
        dataType: 'blog-post-download-skill-button',
        location: 'blog-post-download-skill-section',
        pageName: 'blog-post',
      }}
      newTrackingProps={{
        interaction: {
          element: 'download-skill-button',
          elementText: 'Download',
          location: 'blog-post-download-skill-section',
          detail: activeTab,
        },
        pageType: 'blog-post',
      }}
    >
      <span className="flex items-center gap-1.5">
        <Icon name="download" className="size-3" />
        Download
      </span>
    </ButtonV2>
  )

  const viewGuideButton = (isFullscreenView: boolean) => (
    <button
      type="button"
      onClick={toggleFullscreen}
      className={cn(
        'flex h-8 shrink-0 animate-attention-glow items-center gap-1.5 whitespace-nowrap rounded-[4px] border border-green-light bg-[#ffffff0a] px-4 text-[12px] font-medium transition duration-300 hover:bg-[#ffffff1a] hover:text-moss-white',
        isFullscreenView ? 'text-moss-white' : 'text-grey-100',
      )}
    >
      {isFullscreenView ? 'Close guide' : 'View full video guide'}
      {!isFullscreenView && <Icon name="right-arrow" className="size-3" />}
    </button>
  )

  const windowChrome = (isFullscreenView: boolean) => (
    <motion.div
      ref={isFullscreenView ? undefined : chromeRef}
      layoutId={windowLayoutId}
      transition={{type: 'spring', damping: 32, stiffness: 320}}
      style={isFullscreenView ? undefined : {scale: scrollScale}}
      className={cn(
        'group/window flex flex-col overflow-hidden border border-green-deep bg-green-dark',
        isFullscreenView ? 'fixed inset-0 z-50 h-[100dvh] w-screen' : 'rounded-lg',
      )}
    >
      <div
        className={cn(
          'relative flex h-[41px] shrink-0 items-center gap-2 border-b border-green-deep px-4',
          !isFullscreenView && 'rounded-t-lg',
        )}
      >
        <span className="pointer-events-none absolute inset-x-0 text-center text-[10px] font-medium tracking-widest text-grey-200">
          {getFilename(skillDownloadLink)}
        </span>
        <button
          type="button"
          onClick={toggleFullscreen}
          aria-label={isFullscreenView ? 'Exit fullscreen' : 'View fullscreen'}
          className="ml-auto flex size-8 items-center justify-center rounded-lg text-grey-200/70 transition-colors duration-300 hover:bg-[#ffffff1f] group-hover/window:animate-none group-hover/window:text-moss-white motion-safe:animate-icon-flicker"
        >
          <Icon name="fullscreen" className="size-4" />
        </button>
      </div>
      <div
        className={cn(
          'flex w-full gap-5 overflow-y-auto p-4',
          isFullscreenView
            ? 'flex-1 flex-col items-center gap-8 px-4 pb-6 pt-8 lg:flex-row lg:gap-16 lg:px-24 lg:py-16'
            : 'flex-col',
        )}
      >
        <div
          className={cn(
            'flex flex-col gap-5',
            isFullscreenView && 'order-2 w-full shrink-0 gap-8 lg:order-1 lg:max-w-md',
          )}
        >
          <div className="flex flex-col gap-4">
            {tabSwitcher}
            <div className="flex flex-col gap-2">
              <h3 className="text-[14px] font-semibold text-moss-white">Getting started</h3>
              <ol className="flex flex-col gap-2">
                {steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-[13px] font-normal leading-5 text-moss-white">
                    <span className="font-mono shrink-0 text-[12px] font-medium text-grey-200/90 [font-variant-numeric:slashed-zero]">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {downloadButton}
            {viewGuideButton(isFullscreenView)}
          </div>
        </div>
        {isFullscreenView && (
          <div className="order-1 flex size-full min-w-0 flex-1 items-center justify-center lg:order-2 lg:w-auto">
            <div className="relative aspect-video max-h-full w-full max-w-full overflow-hidden rounded-2xl bg-gradient-to-br from-[#ffffff0f] to-transparent shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)] outline outline-1 outline-[#ffffff1a] lg:h-[90%]">
              <span className="font-mono absolute left-4 top-4 z-10 rounded bg-black/40 px-2 py-1 text-[11px] text-grey-200 backdrop-blur-sm">
                {demoVideos[activeTab].label}
              </span>
              <video
                key={activeTab}
                src={demoVideos[activeTab].src}
                autoPlay
                loop
                muted
                playsInline
                controls
                className="size-full object-contain"
              />
            </div>
          </div>
        )}
      </div>
    </motion.div>
  )

  return (
    <div id={DOWNLOAD_SKILL_ANCHOR} className="flex scroll-mt-16 flex-col gap-4 pt-4 lg:pt-8">
      {!isFullscreen && windowChrome(false)}
      {mounted && createPortal(<AnimatePresence>{isFullscreen && windowChrome(true)}</AnimatePresence>, document.body)}
    </div>
  )
}

const TableSection = ({field}: {field: TableField}) => (
  <PrismicTable
    field={field}
    components={{
      th: ({children}) => (
        <th className="border-b border-l border-moss-black/10 p-3 text-start text-18 font-medium leading-6 text-moss-black first:border-l-0">
          {children}
        </th>
      ),
      td: ({children}) => (
        <td className="border-l border-t border-black/10 p-3 text-start text-15 font-normal leading-5 text-moss-grey-550 first:border-l-0">
          {children}
        </td>
      ),
      table: ({children}) => (
        <div className="w-full overflow-hidden rounded-lg border border-black/10">
          <table className="w-full table-auto border-collapse">{children}</table>
        </div>
      ),
    }}
  />
)
